<template>
    <div class="page-product">
        <Breadcrumbs :elements="breadcrumbs" :isPreloader="isPreloader" />
        <Product
            :slides="slides"
            :product="product"
            :pocketProducts="pocketProducts"
            :isPreloader="isPreloader"
            :isPreloaderCurtain="isPreloaderCurtain"
            @set-param="setParam($event)"
            @set-service="setService"
            @set-extension-direction="setDirection"
            @set-active-extensions="setActiveExtensions"
            @open-modal-content="openModalContent"
            @open-pocket-products="openModalPocketProducts"
        />

        <div class="page-product__container container">
            <div class="page-product__content">
                <div ref="infoCards">
                    <InfoCards :isPreloader="isPreloaderInfoCards" :cards="cards" />
                </div>
                <Tabs :tabs="tabs" :activeTab="activeTab" @set-tab="setActiveTab" />
                <Description
                    v-show="isActiveTab('description')"
                    :title="productDescription.title"
                    :content="productDescription.description"
                />
                <Characteristics
                    v-show="isActiveTab('characteristics')"
                    :title="$t('pageProductTabDescriptionSubTitle')"
                    :characteristics="characteristics"
                />
                <Reviews v-show="isActiveTab('reviews')" :productId="product?.id" />
            </div>

            <div ref="similarProducts">
                <ProductsSlider
                    :title="`${$t('pageProductSameCategoryTitle')} “${parentCategoryTitle || ''}”`"
                    :products="similarProducts"
                    :analyticProducts="similarAnalyticProducts"
                />
            </div>

            <div ref="recentProducts">
                <ProductsSlider
                    v-if="recentProducts?.length"
                    :title="$t('pageProductRecentTitle')"
                    :products="recentProducts"
                    :analyticProducts="recentAnalyticProducts"
                />
            </div>

            <!-- TO DO: Have to add skeletons for component -->
            <InfoSection
                v-if="productAdvantages"
                :theme="'dark'"
                :titleData="{
                    title: productAdvantages?.title,
                    title_background_color: productAdvantages?.title_background_color,
                    title_text_color: productAdvantages?.title_text_color
                }"
                :iconCardBlock="{
                    id: productAdvantages?.id,
                    icons_card_items: productAdvantages?.items
                }"
            />
        </div>

        <StalkingPanel
            :isActive="product?.active"
            :isAvailable="product?.available"
            :isPreloader="isPreloader"
            :price="product?.price"
            :oldPrice="product?.oldPrice"
            :productId="product?.id"
            :services="activeServices"
            :activeExtensions="activeExtensions"
            :extensionDirection="extensionDirection"
            :phone="mainPhoneNumber"
            :analyticProductData="analyticProductData"
            @open-add-to-cart="openAddToCart"
        />

        <ModalAskSpecialist />
        <ModalAddedToCart :pocketProducts="pocketProducts" @open-pocket-products="openModalPocketProducts" />
        <ModalPocketProducts v-if="product" :productId="product.id" :slug="activeModalPocketProductsSlug" />
        <ModalContent :title="activeModalContent?.title" :content="activeModalContent?.description" />
        <EcomMyModal v-if="globalPopupIsShowed && product?.popup" :popup-data-from-product="product?.popup" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import InfoCards from './components/InfoCards';
import StalkingPanel from './components/StalkingPanel';
import Tabs from './components/Tabs';
import Description from './components/Description';
import Characteristics from './components/Characteristics';
import Product from './components/Product';
import Reviews from '@/themes/novaporta/components/modular/Reviews';
import ProductsSlider from '@/themes/novaporta/components/reusable/ProductsSlider';
import Breadcrumbs from '@/themes/novaporta/components/reusable/Breadcrumbs';
import InfoSection from '@/themes/novaporta/components/reusable/InfoSection';

import seo from '~/mixins/seo';
import { urlProduct } from '~/api/seo';

import { createBadgeItem } from '~/themes/novaporta/types/badges';
import { createServiceItem } from '@/themes/novaporta/types/product';
import { createProductAgent } from '@/themes/novaporta/components/reusable/ProductAgent/types';
import {
    createInfoCardItem,
    createProduct,
    createParameterValueItem,
    createParameterItem,
    createPocketProductItem,
    createExtensionItem,
    createExtensionValueItem
} from './types';

import { MODAL_CONTENT, MODAL_ADDED_TO_CART, MODAL_POCKET_PRODUCTS } from '~/themes/novaporta/consts/modals';

import { setRecentProduct, getRecentProducts } from '@/themes/novaporta/utils/products';
import { createBreadcrumbsItem } from '@/themes/novaporta/components/reusable/Breadcrumbs/types';
import eventBus from '@/plugins/event-bus';

export default {
    name: 'PageProduct',
    mixins: [seo],
    data() {
        return {
            analyticProductData: null,
            product: null,
            initialProductData: null,
            productAdvantages: null,
            extensionDirection: null,

            slides: [],
            breadcrumbs: [],
            characteristics: [],
            cards: [],
            activeExtensions: [],

            isPreloader: true,
            isPreloaderCurtain: false,
            isPreloaderInfoCards: true,
            isPreloaderProductsAdavantages: true,

            activeTab: null,
            activeModalContent: null,
            activeModalPocketProductsSlug: null,
            similarAnalyticProducts: [],
            recentAnalyticProducts: [],
            similarProducts: [null, null, null, null, null, null], // null is used for skeletons
            recentProducts: [null, null, null, null, null, null], // null is used for skeletons

            observerProductsSimilar: null,
            observerProductsRecent: null,
            observerInfoCards: null
        };
    },
    watch: {
        analyticProductData: {
            handler(e) {
                if (process.client) {
                    const product = e?.product;

                    const settings = this.globalSettings;
                    const currency = settings?.project_currency?.toUpperCase() || '';
                    const sendData = {
                        currency: currency,
                        value: Number(product?.price),
                        items: [
                            {
                                item_id: product?.productId,
                                item_name: product?.title,
                                discount: product?.discount || 0,
                                item_brand: product?.brand?.name || '',
                                item_category: e?.breadcrumbs?.[0]?.categoryTitle || '',
                                item_category2: e?.breadcrumbs?.[1]?.categoryTitle || '',
                                item_category3: e?.breadcrumbs?.[2]?.categoryTitle || '',
                                item_category4: e?.breadcrumbs?.[3]?.categoryTitle || '',
                                item_category5: e?.breadcrumbs?.[4]?.categoryTitle || '',
                                price: product?.discount ? Number(product?.old_price) : Number(product?.price),
                                quantity: 1
                            }
                        ]
                    };

                    this.$store.dispatch('google_events/sendItemViewEvent', sendData);
                }
            },
            deep: true
        }
    },
    computed: {
        ...mapGetters({
            mainPhoneNumber: 'global/mainPhoneNumber',
            globalPopupIsShowed: 'global/getGlobalPopupIsShowed',
            globalSettings: 'global/globalSettings'
        }),

        getProductId() {
            return this?.product?.id;
        },

        slugProduct() {
            return this.$route.params.product || null;
        },
        title() {
            return this.initialProductData?.title;
        },
        productDescription() {
            return {
                title: this.initialProductData?.description?.title || '',
                description: this.initialProductData?.description?.description || ''
            };
        },
        haveDescription() {
            return this.initialProductData?.description?.description ? true : false;
        },
        parentCategoryTitle() {
            return this.breadcrumbs.length ? this.breadcrumbs[0]?.title : null;
        },

        tabs() {
            let newTabs = [];
            const descriptionItem = { title: this.$t('pageProductTabDescriptionTitle'), keyword: 'description' };
            const characteristicsItem = { title: this.$t('pageProductTabCharacteristics'), keyword: 'characteristics' };
            const reviewsItem = { title: this.$t('pageProductTabReviewsTitle'), keyword: 'reviews' };

            newTabs = [reviewsItem];
            if (this.characteristics.length) newTabs = [characteristicsItem, ...newTabs];
            if (this.haveDescription) newTabs = [descriptionItem, ...newTabs];
            if (newTabs.length) this.activeTab = newTabs[0].keyword;

            return newTabs;
        },
        pocketProducts() {
            return this.initialProductData?.compatible_categories?.map(c => {
                return createPocketProductItem({
                    id: c.id,
                    name: c.name,
                    img: c.image,
                    slug: c.slug
                });
            });
        },
        activeServices() {
            const newServices = [];

            this.product?.services?.map(s => {
                if (s.active) newServices.push(s.id);
            });

            return newServices;
        }
    },
    methods: {
        isActiveTab(keyword) {
            return this.activeTab === keyword ? true : false;
        },

        setActiveTab(keyword) {
            this.activeTab = keyword;
        },
        setActiveModalContent(title, description) {
            this.activeModalContent = {
                title: title,
                description: description
            };
        },
        setActiveModalPocketProductsSlug(slug) {
            this.activeModalPocketProductsSlug = slug;
        },

        async fetchProduct(slug = this.slugProduct, usePreloaders = true) {
            if (usePreloaders) this.isPreloader = true;

            const redirectToErrorPage = (code, message) => {
                this.$nuxt.error({ statusCode: code, message: message });
            };

            try {
                const { data } = await this.$api.product.getProduct(slug);

                this.analyticProductData = data;

                const description = data.product.seo_description || '';
                const title = data.product.seo_title || '';
                const keywords = data.product.seo_keywords || '';
                const seoData = {};

                if (description) {
                    seoData.description = description;
                }
                if (title) {
                    seoData.title = title;
                }
                if (keywords) {
                    seoData.keywords = keywords;
                }

                if (Object.keys(seoData).length) {
                    seoData.image = data.product?.images[0];
                    this.setDataSEO(seoData);
                } else {
                    await this.fetchSEO(urlProduct(this.slugProduct));
                }
                this.setProductData(data);
                this.activeExtensions = [];
                if (usePreloaders) this.isPreloader = false;

                // if (process.client) this.setRecentProduct(); // TO DO: figure out how to avoid error on server side about localStorage undefined
            } catch (error) {
                try {
                    const request = await this.$axios.$post(
                        'general/slug_check',
                        {
                            new_lang: this.$i18n.locale,
                            current_slug: this.slugProduct,
                            object_type: 'product'
                        },
                        {
                            redirectOnError: true
                        }
                    );

                    this.$router.push({ path: this.localePath({ path: `/product/${request.slug}` }) });
                } catch {
                    const statusCode = error.response ? error.response.status : null;
                    redirectToErrorPage(statusCode, this.$t('globalMessageSomethingWentWrong'));
                }
            }
        },
        async fetchProductSliders() {
            try {
                const { data } = await this.$api.product.getSimilarProductsList(this.product.id);

                if (data.similarProducts) {
                    this.similarAnalyticProducts = data.similarProducts;
                    this.similarProducts = data.similarProducts.map(product => {
                        return createProductAgent({
                            active: product.active,
                            available: product.available,
                            id: product.productId,
                            name: product.title,
                            price: Number(product.price),
                            slug: product?.url,
                            img: product.img,
                            oldPrice: Number(product.old_price),
                            badges: product.badges_products,
                            slides: product?.images
                                ? product?.images?.map(s => {
                                      return { img: s };
                                  })
                                : [],
                            isOnSale: Number(product.old_price) ? true : false
                        });
                    });
                }
            } catch (error) {
                console.error('Failed to fetch sliders:', error);
            }
        },
        async fetchProductRecents() {
            let recentProductsIds = getRecentProducts();

            recentProductsIds = recentProductsIds.filter(id => parseFloat(id) !== this.product.id);

            if (recentProductsIds?.length) {
                const handledRecentProductsIds = recentProductsIds.join(',');

                try {
                    const { data } = await this.$api.products.getProductsByIds(handledRecentProductsIds);

                    if (data) {
                        this.recentAnalyticProducts = data;
                        this.recentProducts = data.map(product => {
                            return createProductAgent({
                                active: product.active,
                                available: product.available,
                                id: product.productId,
                                name: product.title,
                                price: Number(product.price),
                                slug: product?.url,
                                img: product.img,
                                oldPrice: Number(product.old_price),
                                badges: product.badges_products,
                                slides: product?.images
                                    ? product?.images?.map(s => {
                                          return { img: s };
                                      })
                                    : [],
                                isOnSale: Number(product.old_price) ? true : false
                            });
                        });
                    }
                } catch (error) {
                    console.error('Failed to fetch sliders:', error);
                }
            } else {
                this.recentProducts = [];
            }
        },
        async fetchCharacteristics(productId = this.product.id) {
            if (productId) {
                try {
                    const { data } = await this.$api.product.getProductProperties(productId);
                    const allCharacteristics = [];
                    data.properties.filter(p => {
                        p.details.filter(d => allCharacteristics.push(d));
                    });

                    this.characteristics = allCharacteristics.map(c => {
                        return {
                            name: c.title,
                            value: c.title_value
                        };
                    });
                } catch (error) {
                    eventBus.$emit('show-toast', {
                        message: this.$t('globalMessageSomethingWentWrong'),
                        type: 'failure'
                    });
                }
            }
        },
        async fetchInfoCards() {
            console.log('fethchchc');
            this.isPreloaderInfoCards = true;
            try {
                const { data } = await this.$api.advantages.getAdvantagesProduct();
                const baseUrl = this.$config.apiBaseUrl;

                this.cards = data?.advantages?.length
                    ? data.advantages.map(a => {
                          return createInfoCardItem({
                              title: a.title,
                              content: a.description,
                              icon: `${baseUrl}${a.icon}` // Not full path, gluing
                          });
                      })
                    : [];
            } catch (error) {
                this.cards = [];

                eventBus.$emit('show-toast', {
                    message: this.$t('globalMessageSomethingWentWrong'),
                    type: 'failure'
                });
            } finally {
                this.isPreloaderInfoCards = false;
                this.isPreloaderProductsAdavantages = false;
            }
        },
        async fetchProductAdvantages() {
            console.log('fetchProductAdvantages');
            this.isPreloaderProductsAdavantages = true;
            try {
                const { data } = await this.$api.product.getProductAdvantages();

                this.productAdvantages = {
                    id: 1,
                    title: data.title,
                    title_background_color: data.title_background_color,
                    title_text_color: data.title_text_color,
                    items: data.advantages.map((a, idx) => {
                        return {
                            text: a.description,
                            icon: `${process.env.API_BASE_URL}${a.icon}`
                        };
                    })
                };

                this.isPreloaderProductsAdavantages = false;
                this.isPreloaderInfoCards = false;
            } catch (error) {
                eventBus.$emit('show-toast', {
                    message: this.$t('globalMessageSomethingWentWrong'),
                    type: 'failure'
                });
            } finally {
                this.isPreloaderInfoCards = false;
                this.isPreloaderProductsAdavantages = false;
            }
        },

        sectionObserver(observerField, fetchMethod, sectionRef, threshold = 0.5) {
            const applyFetchMethod = entries => {
                entries.forEach(async entry => {
                    if (entry.isIntersecting) {
                        this[fetchMethod]();
                        this[observerField].disconnect();
                    }
                });
            };

            this[observerField] = new IntersectionObserver(applyFetchMethod, {
                root: null,
                rootMargin: '0px',
                threshold: threshold
            });
            this[observerField].observe(this.$refs[sectionRef]);
        },
        openModal(name) {
            this.$store.dispatch('global/open', name);
        },
        openModalPocketProducts(slug) {
            this.setActiveModalPocketProductsSlug(slug);
            this.openModal(MODAL_POCKET_PRODUCTS);
        },

        setProductData({ product, breadcrumbs }) {
            this.initialProductData = product;
            const demoParams = product.parameters?.media_params || [];
            const selectParams = product.parameters?.selects || [];
            const extensionsParams = product?.extensions || [];

            const paramItemData = item => {
                return createParameterItem({
                    id: item.specification.id,
                    name: item.specification.name,
                    description_content: item.description,
                    values: item?.values?.length
                        ? item.values.map(v => {
                              return createParameterValueItem({
                                  id: v.id,
                                  name: v.name,
                                  slug: v.slug,
                                  active: v?.is_selected || false,

                                  img: v.img
                              });
                          })
                        : []
                });
            };
            const extensionItemData = item => {
                return createExtensionItem({
                    id: item.specification.id,
                    name: item.specification.name,
                    description: item.description,
                    type: item.type,
                    values: item?.values?.length
                        ? item.values.map((v, idx) => {
                              return createExtensionValueItem({
                                  id: v.extension_id || idx + 1,
                                  name: v.name,
                                  product_id: v.product_id,
                                  extension_id: v.extension_id,
                                  price: v.price,
                                  multiplicity: v.multiplicity,
                                  keyword: v.keyword || null
                              });
                          })
                        : []
                });
            };

            let handledBreadcrumbs = breadcrumbs?.length
                ? breadcrumbs.map(b => {
                      return createBreadcrumbsItem({
                          route: b.categoryUrl,
                          title: b.categoryTitle
                      });
                  })
                : [];

            handledBreadcrumbs.push(
                createBreadcrumbsItem({
                    route: `/product/${product?.url}`,
                    title: product.title
                })
            );

            this.product = createProduct({
                active: product.active,
                available: product.available,
                id: Number(product.productId),
                name: product.title,
                price: Number(product.price),
                slug: product?.url,
                short_description: product?.short_description,
                code: product.code,
                badges: product?.badges_products?.length
                    ? product.badges_products.map(b => {
                          return createBadgeItem({
                              name: b.name,
                              color: b.text_color,
                              backgroundColor: b.background_color
                          });
                      })
                    : [],
                slides: product?.images?.length
                    ? product?.images?.map((i, idx) => {
                          return { id: idx + 1, img: i.img };
                      })
                    : [],
                demo_parameters: demoParams?.length
                    ? demoParams.map(dp => {
                          return paramItemData(dp);
                      })
                    : [],
                select_parameters: selectParams?.length
                    ? selectParams.map(sp => {
                          return paramItemData(sp);
                      })
                    : [],
                extensions: extensionsParams?.length
                    ? extensionsParams.map(ex => {
                          return extensionItemData(ex);
                      })
                    : [],
                services: product?.services?.length
                    ? product.services.map(s => {
                          return createServiceItem({
                              id: s.id,
                              name: s.title,
                              is_switch: s.is_switch,
                              description: s.description,
                              active: false
                          });
                      })
                    : [],

                isOnSale: Number(product.old_price) ? true : false,
                img: null,
                oldPrice: Number(product.old_price)
            });
            this.breadcrumbs = handledBreadcrumbs;
            this.slides = product?.images?.length
                ? product?.images?.map((i, idx) => {
                      return { id: idx + 1, img: i.img };
                  })
                : [];
        },
        setRecentProduct() {
            if (this.initialProductData) {
                const id = this.initialProductData?.productId || null;
                if (id) setRecentProduct(id); // is not recursion, is another method
            }
        },
        setDirection(dir) {
            this.extensionDirection = dir;
        },
        setActiveExtensions(extensions) {
            this.activeExtensions = extensions;
        },
        setService({ productId, serviceId, flag }) {
            const newServices = this.product.services.map(s => {
                return { ...s, active: s.id === serviceId ? flag : s.active };
            });

            this.product = {
                ...this.product,
                services: newServices
            };
        },
        openModalContent({ name, description }) {
            this.setActiveModalContent(name, description);
            this.$nextTick(() => {
                this.openModal(MODAL_CONTENT);
            });
        },
        openAddToCart() {
            this.$nextTick(() => {
                this.openModal(MODAL_ADDED_TO_CART);
            });
        },
        subscribeToBackEvent() {
            window.addEventListener('popstate', event => {
                if (this.$route.params.product || null) this.setParam(this.$route.params.product || null);
            });
        },

        async setParam(slug) {
            this.isPreloaderCurtain = true;

            window.history.pushState(null, '', this.localePath(`/product/${slug}`));
            await this.fetchProduct(slug, false);

            this.isPreloaderCurtain = false;
        }
    },
    components: {
        EcomMyModal: () => import('@/components/modals/EcomMyModal'),
        InfoCards,
        StalkingPanel,
        Tabs,
        Description,
        Characteristics,
        Product,
        Reviews,
        ProductsSlider,
        Breadcrumbs,
        InfoSection,

        ModalAddedToCart: () => import('./components/ModalAddedToCart'),
        ModalPocketProducts: () => import('./components/ModalPocketProducts'),
        ModalAskSpecialist: () => import('@/themes/novaporta/components/modals/ModalAskSpecialist'),
        ModalContent: () => import('@/themes/novaporta/components/modals/ModalContent')
    },
    mounted() {
        const recentProductsIds = getRecentProducts();

        this.subscribeToBackEvent();

        if (this?.getProductId) {
            this.sectionObserver('observerProductsRecent', 'fetchProductRecents', 'recentProducts');
        }
    },
    async fetch() {
        await this.fetchProduct(this.slugProduct, true);

        if (this?.getProductId) {
            await this.fetchCharacteristics(this.product.id);
            await this.fetchProductSliders();
            if (this.globalSettings?.advantages_pages?.find(item => 'product_page' === item)) {
                await this.fetchProductAdvantages(); // Have to add in observer
            } else if (this.globalSettings?.advantages_pages?.find(item => 'product_why_we' === item)) {
                await this.fetchInfoCards();
            } else {
                this.isPreloaderInfoCards = false;
                this.isPreloaderProductsAdavantages = false;
            }

            if (process.client) {
                await this.sectionObserver('observerProductsRecent', 'fetchProductRecents', 'recentProducts');
            }
        }
    },
    async asyncData({ i18n, route, app, redirect, $axios, store }) {
        if (process.server) {
            try {
                const request = await $axios.$post('general/slug_check', {
                    new_lang: i18n.locale,
                    current_slug: route.params.product,
                    object_type: 'product'
                });

                redirect({ path: app.localePath({ path: `/product/${request.slug}` }) });
            } catch (error) {}
        }
    },
    beforeDestroy() {
        // if (process.client) {
        this.setRecentProduct();
        // }
    }
};
</script>

<style lang="scss">
@import 'styles';
</style>
